// src/components/LeadView.tsx
import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import OverviewSection from "./OverviewSection";
import MoreInfoSection from "./Moreinformation"; // New Component
import ConvertLeadSection from "./ConvertLead"; // New Component
import { LeadData } from "../../../src/types"; // Adjust path to types.ts
import Loader from "../../components/common/Loader"; // Import the Loader component

const LeadView: React.FC = () => {
  const location = useLocation();
  const { lead_id } = location.state || {};
  const [lead, setLead] = useState<LeadData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("OVERVIEW");

  useEffect(() => {
    if (!lead_id) {
      setError("Lead ID not found.");
      setLoading(false);
      return;
    }
    const fetchLead = async () => { 
      try {
        const token = sessionStorage.getItem("access_token");
        if (!token) {
          setError("Unauthorized: Please log in.");
          setLoading(false);
          return;
        }
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/leads/show`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ id: lead_id }),
          }
        );
        const data = await response.json();
        if (response.ok && data.status === 200) {
          setLead({
            ...data.lead,
            lead_id: data.lead.id.toString(),
          });
        } else {
          setError(data.message || "Failed to fetch lead details.");
        }
      } catch (err) {
        setError("An error occurred while fetching lead data.");
      } finally {
        setLoading(false);
      }
    };
    fetchLead();
  }, [lead_id]);

  if (loading) return <Loader />; // Replace loading text with Loader component
  if (error) return <p className="text-center text-red-500">{error}</p>;

  return (
    <div
      className="max-w-7xl mx-auto text-gray-800 dark:text-gray-100"
      style={{
        fontFamily: "Lato, sans-serif",
        fontSize: "12px",
        fontWeight: "normal",
      }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap"
        rel="stylesheet"
      />

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
        <div className="flex items-center px-0 py-3 justify-between">
          <h4 className="text-theme-xl font-semibold text-gray-800 dark:text-white/90 text-center">
            Lead Details
          </h4>
          <Link
            to="/lead-list"
            className="px-3 py-1.5 bg-[#2f6aa7] dark:bg-[#3d6794] hover:bg-[#2779bd] dark:hover:bg-[#335877] text-sm text-white dark:text-gray-300 rounded-lg flex items-center"
          >
            <ArrowLeft className="inline-block mr-1" size={16} />
            Back
          </Link>
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-gray-100 p-0.5 dark:bg-gray-900">
          <button
            onClick={() => setActiveTab("OVERVIEW")}
            className={`inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md group hover:text-gray-900 dark:hover:text-black ${
              activeTab === "OVERVIEW"
                ? "text-gray-900 dark:text-black bg-white dark:bg-white"
                : "text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700"
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab("MORE INFORMATION")}
            className={`inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md group hover:text-gray-900 dark:hover:text-black ${
              activeTab === "MORE INFORMATION"
                ? "text-gray-900 dark:text-black bg-white dark:bg-white"
                : "text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700"
            }`}
          >
            More Information
          </button>
          <button
            onClick={() => setActiveTab("CONVERT LEAD")}
            className={`inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md group hover:text-gray-900 dark:hover:text-black ${
              activeTab === "CONVERT LEAD"
                ? "text-gray-900 dark:text-black bg-white dark:bg-white"
                : "text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700"
            }`}
          >
            Convert Lead
          </button>
        </div>
        {activeTab === "OVERVIEW" && lead && <OverviewSection lead={lead} />}
        {activeTab === "MORE INFORMATION" && lead && (
          <MoreInfoSection lead={lead} />
        )}
        {activeTab === "CONVERT LEAD" && lead && (
          <ConvertLeadSection lead={lead} />
        )}
      </div>
    </div>
  );
};

export default LeadView;
